import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of, switchMap } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Modal } from '@spirit-testing/component-library';

import { CustomerDetailService } from '../services/customer-detail.service';
import { AccountService } from '../account/account.service';
import { TenantUserService } from '../services/tenant-user.service';
import { CustomerDTO, CustomerUserDTO, InvitationDTO, RoleDTO, TenantUserStatus } from '../models/customer.model';
import { ChangeRoleDialogComponent } from '../shared/change-role-dialog/change-role-dialog.component';
import { UserRowActionsMenuComponent } from './user-row-actions-menu/user-row-actions-menu.component';
import { MAIL_NOT_DELIVERED_SNACKBAR } from './invitation-mail-snackbar';
import { PageHeaderComponent } from '../components/page-header/page-header.component';
import { StatGroupComponent, StatItem } from '../components/stat-group/stat-group.component';
import { SearchBarComponent } from '../components/search-bar/search-bar.component';
import { OverviewPaginationComponent } from '../components/overview-pagination/overview-pagination.component';
import { AvatarComponent } from '../components/avatar/avatar.component';
import { StatusPillComponent, StatusKind } from '../components/status-pill/status-pill.component';
import { RoleBadgeComponent } from '../components/role-badge/role-badge.component';
import { ScrollContainerDirective } from '../shared/scroll-container/scroll-container.directive';
import { ASSIGNABLE_TENANT_ROLE_CODES } from '../shared/tenant-roles';

interface BlockingProject {
  id: string;
  name: string;
}

const DEFAULT_PAGE_SIZE = 10;

@Component({
  selector: 'app-customer-detail',
  imports: [
    TranslatePipe,
    MatTableModule,
    MatProgressSpinnerModule,
    MatIconModule,
    DatePipe,
    ChangeRoleDialogComponent,
    UserRowActionsMenuComponent,
    PageHeaderComponent,
    StatGroupComponent,
    SearchBarComponent,
    OverviewPaginationComponent,
    Modal,
    AvatarComponent,
    StatusPillComponent,
    RoleBadgeComponent,
    ScrollContainerDirective,
  ],
  templateUrl: './customer-detail.component.html',
  styleUrl: './customer-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly customerDetailService = inject(CustomerDetailService);
  private readonly accountService = inject(AccountService);
  private readonly tenantUserService = inject(TenantUserService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);

  customer = signal<CustomerDTO | null>(null);
  users = signal<CustomerUserDTO[]>([]);
  loading = signal(true);
  error = signal<string | null>(null);

  isPlatformAdmin = signal(false);
  roles = signal<RoleDTO[]>([]);
  selectedUser = signal<CustomerUserDTO | null>(null);
  showRoleDialog = signal(false);

  readonly userToDelete = signal<CustomerUserDTO | null>(null);
  readonly showDeleteDialog = signal(false);
  readonly deleting = signal(false);
  readonly blockingProjects = signal<BlockingProject[]>([]);

  readonly searchTerm = signal('');
  readonly pageIndex = signal(0);
  readonly pageSize = signal(DEFAULT_PAGE_SIZE);

  readonly filteredUsers = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    if (!term) return this.users();
    return this.users().filter(
      (user) => user.displayName.toLowerCase().includes(term) || user.email.toLowerCase().includes(term),
    );
  });

  readonly pagedUsers = computed(() => {
    const data = this.filteredUsers();
    if (data.length === 0) return data;
    const size = this.pageSize();
    const maxIndex = Math.max(0, Math.ceil(data.length / size) - 1);
    const start = Math.min(this.pageIndex(), maxIndex) * size;
    return data.slice(start, start + size);
  });

  readonly stats = computed((): StatItem[] => [
    { label: this.translate.instant('customerDetail.stats.total'), value: this.users().length },
  ]);

  readonly noDataMessageKey = computed(() =>
    this.searchTerm().trim() ? 'customerDetail.table.noResults' : 'customerDetail.table.noData',
  );

  private readonly customerId = signal('');
  private readonly currentUserId = signal('');

  readonly displayedColumns = computed(() =>
    this.isPlatformAdmin()
      ? ['name', 'email', 'lastLogin', 'role', 'status', 'invitationValid', 'action']
      : ['name', 'email', 'lastLogin', 'role', 'status', 'invitationValid'],
  );

  readonly resentInvitationIds = signal<ReadonlySet<string>>(new Set());
  readonly resendingInvitationIds = signal<ReadonlySet<string>>(new Set());

  private readonly deleteDialogKeyBase = computed(() => {
    const user = this.userToDelete();
    return this.deleteKeyBase(!!user && this.isInvitationRow(user));
  });

  readonly deleteTitleKey = computed(() => `${this.deleteDialogKeyBase()}.confirmTitle`);
  readonly deleteMessageKey = computed(() => `${this.deleteDialogKeyBase()}.confirmMessage`);
  readonly deleteConfirmButtonKey = computed(() => `${this.deleteDialogKeyBase()}.confirmButton`);

  statusKind(status?: TenantUserStatus | string, expired?: boolean): StatusKind {
    if (status === 'INACTIVE') return 'inaktiv';
    if (expired || status === 'EXPIRED') return 'abgelaufen';
    if (status === 'PENDING') return 'pending';
    return 'aktiv';
  }

  isInvitationExpired(user: Pick<CustomerUserDTO, 'status' | 'expired'>): boolean {
    return user.expired === true || user.status === 'EXPIRED';
  }

  /**
   * Die Tabelle mischt echte Benutzer und Einladungen. Hinter einer Einladungs-Zeile steht noch
   * kein Benutzer — ihre id ist die der Einladung, daher laufen Aktionen darauf ueber den
   * Einladungs-Pfad statt ueber den Benutzer-Pfad.
   *
   * Unterschieden wird ueber die Einladungs-Gueltigkeit: nur Einladungs-Zeilen tragen ein
   * expiresAt. Der Status taugt nicht, weil auch ein echter Benutzer auf PENDING stehen kann.
   */
  isInvitationRow(user: Pick<CustomerUserDTO, 'expiresAt'>): boolean {
    return !!user.expiresAt;
  }

  canResendInvitation(user: CustomerUserDTO): boolean {
    return this.isInvitationRow(user);
  }

  removeActionLabelKey(user: CustomerUserDTO): string {
    return this.isInvitationRow(user) ? 'customerDetail.actions.removeInvitation' : 'customerDetail.actions.removeUser';
  }

  isInvitationResent(userId: string): boolean {
    return this.resentInvitationIds().has(userId);
  }

  isInvitationResending(userId: string): boolean {
    return this.resendingInvitationIds().has(userId);
  }

  onResendInvitation(user: CustomerUserDTO): void {
    const customerId = this.customerId();
    if (!customerId || this.isInvitationResent(user.id) || this.isInvitationResending(user.id)) return;

    this.resendingInvitationIds.set(new Set([...this.resendingInvitationIds(), user.id]));
    this.tenantUserService.resendInvitation(customerId, user.id).subscribe({
      next: (invitation) => {
        if (invitation.mailDelivered === false) {
          this.snackBar.open(
            this.translate.instant('customerDetail.actions.resendMailNotDelivered'),
            undefined,
            MAIL_NOT_DELIVERED_SNACKBAR,
          );
        } else {
          this.resentInvitationIds.set(new Set([...this.resentInvitationIds(), user.id, invitation.id]));
        }
        this.showReplacementInvitation(customerId, user.id, invitation);
      },
      error: () => {
        this.removeResendingId(user.id);
        this.snackBar.open(this.translate.instant('customerDetail.actions.resendError'), undefined, {
          panelClass: 'snackbar-error',
        });
      },
    });
  }

  // Der Resend ersetzt die Einladung durch eine neue mit eigener ID; ein weiterer Resend auf die
  // alte ID scheitert am Backend. Bis die Liste neu geladen ist, bleibt die Zeile gesperrt.
  private showReplacementInvitation(customerId: string, previousId: string, replacement: InvitationDTO): void {
    this.tenantUserService.getUsers(customerId).subscribe({
      next: (users) => {
        this.users.set(users);
        this.removeResendingId(previousId);
      },
      error: () => {
        this.users.set(this.users().map((u) => (u.id === previousId ? { ...u, id: replacement.id } : u)));
        this.removeResendingId(previousId);
      },
    });
  }

  private removeResendingId(userId: string): void {
    const remaining = new Set(this.resendingInvitationIds());
    remaining.delete(userId);
    this.resendingInvitationIds.set(remaining);
  }

  fullName(user: CustomerUserDTO): string {
    const first = (user.firstName ?? '').trim();
    const last = (user.lastName ?? '').trim();
    const joined = `${first} ${last}`.trim();
    return joined || user.displayName || '';
  }

  ngOnInit(): void {
    const customerId = this.route.snapshot.paramMap.get('customerId') ?? '';
    this.customerId.set(customerId);
    this.loading.set(true);
    this.error.set(null);

    this.accountService
      .getProfile()
      .pipe(
        switchMap((profile) => {
          const isPlatformAdmin = profile.role === 'PLATFORM_ADMIN';
          this.isPlatformAdmin.set(isPlatformAdmin);
          this.currentUserId.set(profile.id);
          return forkJoin({
            customer: this.customerDetailService.getCustomer(customerId),
            users: this.tenantUserService.getUsers(customerId),
            roles: isPlatformAdmin ? this.tenantUserService.getRoles(customerId) : of([]),
          });
        }),
      )
      .subscribe({
        next: ({ customer, users, roles }) => {
          this.customer.set(customer);
          this.users.set(users);
          this.roles.set((roles as RoleDTO[]).filter((r) => ASSIGNABLE_TENANT_ROLE_CODES.includes(r.code)));
          this.loading.set(false);
        },
        error: () => {
          this.error.set('customerDetail.table.error');
          this.loading.set(false);
        },
      });
  }

  onSearchChange(term: string): void {
    this.searchTerm.set(term);
    this.pageIndex.set(0);
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.pageIndex.set(0);
  }

  isOwnUser(userId: string): boolean {
    return this.currentUserId() === userId;
  }

  onChangeRole(user: CustomerUserDTO): void {
    this.navigateToUserEdit(user, 'rolle');
  }

  onRoleSave(roleId: string): void {
    const user = this.selectedUser();
    const customerId = this.customerId();
    if (!user || !customerId) return;

    this.tenantUserService.assignPlatformRole(customerId, user.id, roleId).subscribe({
      next: (updated) => {
        this.users.set(this.users().map((u) => (u.id === updated.id ? updated : u)));
        this.showRoleDialog.set(false);
        this.snackBar.open(this.translate.instant('settings.roleChangeSuccess'), undefined, {
          panelClass: 'snackbar-success',
        });
      },
      error: (err) => {
        const key = err?.status === 409 ? 'settings.lastAdminError' : 'settings.roleChangeError';
        this.snackBar.open(this.translate.instant(key), undefined, {
          panelClass: 'snackbar-error',
        });
      },
    });
  }

  onRoleDialogCancel(): void {
    this.showRoleDialog.set(false);
  }

  onInviteUser(): void {
    void this.router.navigate(['/client-management', this.customerId(), 'users', 'neu']);
  }

  onViewProfile(user: CustomerUserDTO): void {
    this.navigateToUserEdit(user);
  }

  private navigateToUserEdit(user: CustomerUserDTO, tab?: 'daten' | 'rolle' | 'praeferenzen'): void {
    void this.router.navigate(['/client-management', this.customerId(), 'users', user.id, 'bearbeiten'], {
      queryParams: tab ? { tab } : undefined,
    });
  }

  onRemoveUser(user: CustomerUserDTO): void {
    this.userToDelete.set(user);
    this.blockingProjects.set([]);
    this.showDeleteDialog.set(true);
  }

  onDeleteConfirmed(): void {
    const user = this.userToDelete();
    const customerId = this.customerId();
    if (!user || !customerId || this.deleting()) return;

    const isInvitation = this.isInvitationRow(user);
    const removal$ = isInvitation
      ? this.tenantUserService.revokeInvitation(customerId, user.id)
      : this.tenantUserService.delete(customerId, user.id);

    this.deleting.set(true);
    removal$.subscribe({
      next: () => {
        this.users.set(this.users().filter((u) => u.id !== user.id));
        this.deleting.set(false);
        this.closeDeleteDialog();
        this.snackBar.open(this.translate.instant(`${this.deleteKeyBase(isInvitation)}.success`), undefined, {
          panelClass: 'snackbar-success',
        });
      },
      error: (err: HttpErrorResponse) => {
        this.deleting.set(false);
        // Nur der Benutzer-Loeschpfad kennt blockierende Projekte; beim Widerruf ist 409 ein
        // Statuskonflikt (Einladung inzwischen angenommen) und wird als Fehler gemeldet.
        if (err.status === 409 && !isInvitation) {
          const projects = (err.error?.blockingProjects as BlockingProject[] | undefined) ?? [];
          this.blockingProjects.set(projects);
          return;
        }
        const key = this.deleteErrorKey(err.status, isInvitation);
        this.snackBar.open(this.translate.instant(key), undefined, { panelClass: 'snackbar-error' });
        this.closeDeleteDialog();
      },
    });
  }

  onDeleteCancelled(): void {
    if (this.deleting()) return;
    this.closeDeleteDialog();
  }

  private closeDeleteDialog(): void {
    this.showDeleteDialog.set(false);
    this.userToDelete.set(null);
    this.blockingProjects.set([]);
  }

  private deleteKeyBase(isInvitation: boolean): string {
    return isInvitation ? 'customerDetail.delete.invitation' : 'customerDetail.delete';
  }

  private deleteErrorKey(status: number, isInvitation: boolean): string {
    const base = this.deleteKeyBase(isInvitation);
    // 400 (Selbstloeschung) gibt es nur auf dem Benutzer-Pfad, 409 (bereits angenommen) nur beim
    // Widerruf — der Rest ist fuer beide gleich.
    if (status === 400 && !isInvitation) return `${base}.errorSelf`;
    if (status === 409 && isInvitation) return `${base}.errorAccepted`;
    switch (status) {
      case 403:
        return `${base}.errorForbidden`;
      case 404:
        return `${base}.errorNotFound`;
      default:
        return `${base}.errorGeneric`;
    }
  }
}
